import type { BookLanguage } from "@contract";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/control/Button";
import { StateChip } from "../../../components/control/StateChip";
import { Card } from "../../../components/data/Card";
import { KeyValueList } from "../../../components/data/KeyValueList";
import { Bytes } from "../../../components/format/Bytes";
import { DateTime } from "../../../components/format/DateTime";
import { useBookContext } from "../bookContext";
import { BookCover } from "./BookCover";
import { BookMetadataForm } from "./BookMetadataForm";
import { DeleteBook } from "./DeleteBook";
import { RenderSettingsForm } from "./RenderSettingsForm";

function BookLanguageName({ language }: { language: BookLanguage }) {
  const { t } = useTranslation("common");
  return <>{language === "de" ? t("bookLanguage.de") : t("bookLanguage.en")}</>;
}

/** What the Book is: its Source, its language, its author, and where it stands. */
export function BookFactsCard() {
  const { t } = useTranslation("overview");
  const { book } = useBookContext();
  const [editing, setEditing] = useState(false);
  // Built outside the JSX: the item keys are identifiers, not words, and the
  // literal-string lint reads every literal inside a prop as text.
  const items = [
    {
      key: "author",
      label: t("book.author"),
      value: book.authorName ?? t("book.noAuthor"),
    },
    {
      key: "language",
      label: t("book.language"),
      value: book.language ? <BookLanguageName language={book.language} /> : "—",
    },
    {
      key: "state",
      label: t("book.state"),
      value: <StateChip kind="book" state={book.state} />,
    },
    {
      key: "source",
      label: t("book.source"),
      value: (
        <span className="flex min-w-0 flex-col">
          <span className="truncate">{book.source.filename}</span>
          <span className="text-ink-3">
            <Bytes value={book.source.sizeBytes} />
          </span>
        </span>
      ),
    },
    {
      key: "uploaded",
      label: t("book.uploaded"),
      value: <DateTime value={book.createdAt} style="dateTime" />,
    },
  ];
  return (
    <Card
      label={t("book.label")}
      title={book.title ?? t("book.untitled")}
      titleSize="sm"
      className="min-w-0 [&_h2]:break-words"
    >
      <div id="book-details" className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <Button variant={!editing ? "secondary" : "quiet"} onClick={() => setEditing(false)}>
            {t("edit.heading")}
          </Button>
          <Button variant={editing ? "secondary" : "quiet"} onClick={() => setEditing(true)}>
            {t("nav:workspace.settings")}
          </Button>
        </div>
        {editing ? <RenderSettingsForm /> : <BookMetadataForm />}
        <KeyValueList
          layout="columns"
          labelWidth={104}
          items={items.filter((item) => ["state", "source", "uploaded"].includes(item.key))}
        />
        <BookCover />
        <DeleteBook />
      </div>
    </Card>
  );
}
